import { useCallback, useEffect, useState } from "react";
import { startOfMonth, format } from "date-fns";
import { toast } from "sonner";
import { Lock, Unlock, User } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, ymd, plDate } from "@/lib/api";
import { MonthCalendar } from "@/components/MonthCalendar";

const SlotButton = ({ s, onToggle }) => {
  if (s.status === "booked") return (
    <div data-testid={`admin-slot-${s.time}`} className="p-3 rounded-md border border-primary/40 bg-primary/10 text-sm">
      <div className="font-semibold text-primary">{s.time}</div>
      <div className="flex items-center gap-1 text-xs text-white/75 mt-1"><User size={12} /> {s.booking.name}</div>
    </div>
  );
  const blocked = s.status === "blocked";
  return (
    <button data-testid={`admin-slot-${s.time}`} onClick={() => onToggle(s)}
      className={`p-3 rounded-md border text-sm text-left transition-colors ${blocked ? "border-red-400/40 bg-red-500/10" : "border-white/15 hover:border-primary/60"}`}>
      <div className="font-semibold">{s.time}</div>
      <div className="flex items-center gap-1 text-xs mt-1 text-white/60">{blocked ? <><Lock size={12} /> Zablokowany</> : <><Unlock size={12} /> Wolny</>}</div>
    </button>
  );
};

export const ScheduleTab = () => {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [meta, setMeta] = useState({ counts: {}, blocked_days: [] });
  const [date, setDate] = useState(ymd(new Date()));
  const [day, setDay] = useState(null);

  const loadMonth = useCallback(() => api.get("/admin/month", { params: { month: format(month, "yyyy-MM") } }).then((r) => setMeta(r.data)), [month]);
  const loadDay = useCallback(() => api.get("/admin/day", { params: { date } }).then((r) => setDay(r.data)), [date]);
  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadDay(); }, [loadDay]);

  const call = async (path, body) => {
    try { await api.post(path, body); await Promise.all([loadDay(), loadMonth()]); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleSlot = (s) => call(s.status === "blocked" ? "/admin/blocks/remove" : "/admin/blocks", { date, time: s.time });
  const toggleDay = (v) => call(v ? "/admin/blocks" : "/admin/blocks/remove", { date, time: null });

  return (
    <div data-testid="admin-schedule-tab" className="grid lg:grid-cols-[420px_1fr] gap-6">
      <div className="panel p-6">
        <MonthCalendar month={month} onMonthChange={setMonth} selected={date} onSelect={setDate}
          dayClass={(ds, sel) => (!sel && meta.blocked_days.includes(ds) ? "bg-red-500/15 text-red-300" : "")}
          renderMeta={(ds) => meta.counts[ds] ? <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-primary" /> : null} />
        <p className="text-xs text-muted-foreground mt-5">Kropka = rezerwacje w danym dniu. Czerwony = cały dzień zablokowany.</p>
      </div>
      <div className="panel p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h3 data-testid="admin-day-title" className="font-display text-xl font-semibold">{plDate(date)}</h3>
            {day && !day.working_day && <p className="text-xs text-white/50 mt-1">Dzień wolny (wg ustawień dni pracy)</p>}
          </div>
          <label className="flex items-center gap-3 text-sm">Zablokuj cały dzień
            <Switch data-testid="admin-block-day-switch" checked={!!day?.day_blocked} onCheckedChange={toggleDay} />
          </label>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Kliknij godzinę, aby ją zablokować lub odblokować. Zablokowane terminy klient widzi jako zajęte.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {day?.slots.map((s) => <SlotButton key={s.time} s={s} onToggle={toggleSlot} />)}
        </div>
      </div>
    </div>
  );
};
